import { useState, useMemo } from "react";
import {
  useGetClassesOfTeacher,
  edlinkCloneClassroom,
  getClass,
} from "@packages/api/madlenAPI";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  CheckCircle2,
  Search,
  Users,
  School,
  ChevronDown,
  ChevronUp,
  Link,
} from "lucide-react";
import {
  EdlinkClassroomCloneRequestDto,
  EdlinkClassroomResponseDto,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import { CourseData } from "@packages/constant/CourseData";
import { useToast } from "@packages/contexts/ToastContext";
import { getCurrentTeacher } from "@packages/store/userSlice";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { Button, Text } from "@core/index";
import { StudentList } from "./StudentList";
import Select, { SingleValue } from "react-select";

// Define types for select options
interface GradeOption {
  value: string;
  label: string;
}

interface CourseOption {
  value: GetTopicsCourseType;
  label: string;
}

const EdlinkTeacherClassesView = () => {
  const { t } = useTranslation();
  const { data: classes, isLoading } = useGetClassesOfTeacher();
  getClass;
  const madlenClassrooms = useAppSelector(
    (state) => state.user.teacher?.classes || [],
  );
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<EdlinkClassroomCloneRequestDto[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedClassId, setExpandedClassId] = useState<string | null>(null);
  const { addToast } = useToast();
  const [isImportLoading, setIsImportLoading] = useState(false);

  const grades: GradeOption[] = ["5", "6", "7", "8", "9", "10", "11", "12"].map(
    (grade) => ({
      value: grade,
      label: t("classes.gradeWithNumber", { number: grade }),
    }),
  );

  const filteredClasses: EdlinkClassroomResponseDto[] = useMemo(() => {
    if (!classes) return [];
    return classes
      .filter((classItem: EdlinkClassroomResponseDto) =>
        classItem.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((a, b) => {
        const isAImported = madlenClassrooms.some(
          (madlenClass) => madlenClass.edlinkClassId === a.id,
        );
        const isBImported = madlenClassrooms.some(
          (madlenClass) => madlenClass.edlinkClassId === b.id,
        );
        if (isAImported && !isBImported) return 1;
        if (!isAImported && isBImported) return -1;
        return 0;
      });
  }, [classes, searchQuery]);

  const isClassComplete = (classData: EdlinkClassroomCloneRequestDto) => {
    return classData?.grade && classData?.courseType;
  };

  const isAllComplete = useMemo(() => {
    if (!filteredClasses?.length) return false;
    return filteredClasses.every((classItem: EdlinkClassroomResponseDto) => {
      const classData = formData.find(
        (data) => data.edlinkClassroomId === classItem.id,
      );
      return isClassComplete(classData);
    });
  }, [filteredClasses, formData]);

  const handleGlobalSubmit = async () => {
    try {
      setIsImportLoading(true);
      await edlinkCloneClassroom(
        formData.filter((data) => data.grade && data.courseType),
      );
      addToast({
        title: t("classes.importSuccess"),
        variant: "success",
        duration: 3000,
      });
      await dispatch(getCurrentTeacher());
      setIsImportLoading(false);
    } catch (error) {
      setIsImportLoading(false);
      console.error(error);
      addToast({
        title: t("classes.importError"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Import All */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:w-96">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                     placeholder:text-gray-500 dark:placeholder:text-gray-400
                     focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                     dark:focus:ring-primary-400/20 dark:focus:border-primary-400"
            placeholder={t("classes.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="solid"
          size="md"
          rounded="lg"
          className="w-full sm:w-auto"
          bgColor="bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-600"
          textColor="text-white"
          onClick={handleGlobalSubmit}
          loading={isImportLoading}
          leftIcon={<School className="w-4 h-4" />}
        >
          <span>{t("classes.importAll")}</span>
          <span className="ml-2 px-2 py-0.5 text-xs bg-white/20 rounded-full">
            {formData.filter((data) => data.grade && data.courseType).length}/
            {filteredClasses.length}
          </span>
        </Button>
      </div>

      {/* Classes Grid */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2
                    scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 
                    scrollbar-track-transparent pt-2 pl-2"
      >
        {filteredClasses.map((classItem) => {
          const classData = formData.find(
            (data) => data.edlinkClassroomId === classItem.id,
          ) || {
            edlinkClassroomId: classItem.id,
            grade: null,
            courseType: undefined,
          };
          const isComplete = isClassComplete(classData);

          const courses: CourseOption[] = Object.entries(CourseData)
            .filter(([, value]) => value.visible(classData.grade ?? 5))
            .map(([key, value]) => ({
              value: key as GetTopicsCourseType,
              label: t(`courseType.${value.name(classData.grade ?? 5)}`),
            }));

          return (
            <div
              key={classItem.id}
              className={`rounded-lg p-4
                       border ${
                         isComplete
                           ? "border-green-200 dark:border-green-700 bg-green-100 dark:bg-green-900"
                           : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                       }
                       transition-all duration-200 hover:shadow-md
                       relative`}
            >
              {/* Status Indicator */}
              <div className="absolute -top-2 -right-2">
                {isComplete ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                )}
              </div>

              {/* Already Imported Indicator */}
              {madlenClassrooms.some(
                (madlenClass) => madlenClass.edlinkClassId === classItem.id,
              ) && (
                <div className="absolute -top-2 -left-2 flex items-center gap-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800">
                  <Link className="w-3 h-3" />
                  {t("classes.alreadyImported")}
                </div>
              )}

              <div className="space-y-4">
                {/* Class Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {classItem.name}
                    </h3>
                    <Button
                      variant="solid"
                      size="md"
                      rounded="lg"
                      textColor="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      className="mt-1"
                      leftIcon={<Users className="w-4 h-4" />}
                      rightIcon={
                        expandedClassId === classItem.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      }
                      onClick={() =>
                        setExpandedClassId(
                          expandedClassId === classItem.id
                            ? null
                            : classItem.id!,
                        )
                      }
                    >
                      <span>
                        {classItem.references?.enrollments || 0}{" "}
                        {t("classes.participants")}
                      </span>
                    </Button>
                  </div>
                </div>

                {/* Student List */}
                {expandedClassId === classItem.id && (
                  <StudentList classId={classItem.id!} />
                )}

                {/* Selection Controls */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Select
                      options={grades}
                      value={
                        grades.find(
                          (grade) =>
                            grade.value === classData.grade?.toString(),
                        ) || null
                      }
                      onChange={(newValue: SingleValue<GradeOption>) => {
                        const newGrade = newValue
                          ? Number(newValue.value)
                          : null;
                        setFormData((prev) => {
                          const existingIndex = prev.findIndex(
                            (data) => data.edlinkClassroomId === classItem.id,
                          );
                          if (existingIndex >= 0) {
                            const newData = [...prev];
                            newData[existingIndex] = {
                              ...newData[existingIndex],
                              grade: newGrade,
                            };
                            return newData;
                          }
                          return [
                            ...prev,
                            {
                              edlinkClassroomId: classItem.id,
                              grade: newGrade,
                            } as EdlinkClassroomCloneRequestDto,
                          ];
                        });
                      }}
                      placeholder={t("classes.selectGrade")}
                      classNamePrefix="react-select"
                      className={`text-sm ${
                        isComplete
                          ? "border-green-200 dark:border-green-600"
                          : "border-gray-200 dark:border-gray-600"
                      }`}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: isComplete
                            ? "rgba(209, 250, 229, 0.5)"
                            : "rgba(249, 250, 251, 0.5)",
                          borderColor: isComplete
                            ? "rgb(187, 247, 208)"
                            : "rgb(229, 231, 235)",
                          "&:hover": {
                            borderColor: isComplete
                              ? "rgb(167, 243, 208)"
                              : "rgb(209, 213, 219)",
                          },
                          boxShadow: "none",
                          borderRadius: "0.375rem",
                          padding: "0.25rem",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "rgb(255, 255, 255)",
                          borderRadius: "0.375rem",
                          marginTop: "0.25rem",
                        }),
                        option: (base, { isFocused, isSelected }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "rgb(59, 130, 246)"
                            : isFocused
                              ? "rgb(229, 231, 235)"
                              : "rgb(255, 255, 255)",
                          color: isSelected ? "white" : "rgb(17, 24, 39)",
                          cursor: "pointer",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "rgb(17, 24, 39)",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgb(107, 114, 128)",
                        }),
                      }}
                    />
                  </div>

                  <div>
                    <Select
                      options={courses}
                      value={
                        courses.find(
                          (course) => course.value === classData.courseType,
                        ) || null
                      }
                      onChange={(newValue: SingleValue<CourseOption>) => {
                        const newCourse = newValue ? newValue.value : undefined;
                        setFormData((prev) => {
                          const existingIndex = prev.findIndex(
                            (data) => data.edlinkClassroomId === classItem.id,
                          );
                          if (existingIndex >= 0) {
                            const newData = [...prev];
                            newData[existingIndex] = {
                              ...newData[existingIndex],
                              grade: classData.grade ?? 5,
                              courseType: newCourse,
                            };
                            return newData;
                          }
                          return [
                            ...prev,
                            {
                              edlinkClassroomId: classItem.id,
                              grade: classData.grade ?? 5,
                              courseType: newCourse,
                            },
                          ];
                        });
                      }}
                      placeholder={t("classes.selectCourse")}
                      classNamePrefix="react-select"
                      className={`text-sm ${
                        isComplete
                          ? "border-green-200 dark:border-green-600"
                          : "border-gray-200 dark:border-gray-600"
                      }`}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: isComplete
                            ? "rgba(209, 250, 229, 0.5)"
                            : "rgba(249, 250, 251, 0.5)",
                          borderColor: isComplete
                            ? "rgb(187, 247, 208)"
                            : "rgb(229, 231, 235)",
                          "&:hover": {
                            borderColor: isComplete
                              ? "rgb(167, 243, 208)"
                              : "rgb(209, 213, 219)",
                          },
                          boxShadow: "none",
                          borderRadius: "0.375rem",
                          padding: "0.25rem",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "rgb(255, 255, 255)",
                          borderRadius: "0.375rem",
                          marginTop: "0.25rem",
                        }),
                        option: (base, { isFocused, isSelected }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "rgb(59, 130, 246)"
                            : isFocused
                              ? "rgb(229, 231, 235)"
                              : "rgb(255, 255, 255)",
                          color: isSelected ? "white" : "rgb(17, 24, 39)",
                          cursor: "pointer",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "rgb(17, 24, 39)",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgb(107, 114, 128)",
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredClasses.length === 0 && (
        <div className="text-center py-8">
          <Text variant="p" status="secondary" size={"base"}>
            {searchQuery ? "classes.noSearchResults" : "classes.noClassesFound"}
          </Text>
        </div>
      )}
    </div>
  );
};

export default EdlinkTeacherClassesView;
